import React from 'react';
import { env } from '../../../config';
import { FooterConfig, HeaderConfig } from '../../../utils/api/pdfLayout';

type Variant = 'header' | 'footer';
type Align = 'left' | 'center' | 'right';

// each element sits in its own third of the band, so "center" is the centre of
// the whole band and not of the space left over by the other element
const CELL: Record<Align, React.CSSProperties> = {
  left: { gridColumn: 1, justifySelf: 'start' },
  center: { gridColumn: 2, justifySelf: 'center' },
  right: { gridColumn: 3, justifySelf: 'end' },
};

interface Props {
  variant: Variant;
  config: HeaderConfig | FooterConfig;
  logoGuid?: string | null;
}

const HeaderFooterBlock: React.FC<Props> = ({ variant, config, logoGuid }) => {
  const isHeader = variant === 'header';
  const header = config as HeaderConfig;

  // matches the backend: "none" is not a border
  const border =
    isHeader && header.border !== 'none' ? header.border : undefined;
  const showLogo =
    isHeader && (header.mode === 'logo_and_text' || header.mode === 'logo');
  const showText =
    (isHeader && (header.mode === 'logo_and_text' || header.mode === 'text')) ||
    (!isHeader && !!config.text);
  const logoAlign = (header.logoJustifyContent ?? 'left') as Align;
  const textAlign = (header.textStyle?.justifyContent ?? 'left') as Align;
  const split = showLogo && showText && !!header.isJustifySeperate;
  const separate = split && logoAlign !== textAlign;

  const logoHeight = Number(header?.logoStyle?.height);
  const logoWidth = Number(header?.logoStyle?.width);
  const hasWidth = Number.isFinite(logoWidth) && logoWidth > 0;
  const hasHeight = Number.isFinite(logoHeight) && logoHeight > 0;

  const isCustomLogoStyle = hasWidth || hasHeight;

  const logo =
    showLogo &&
    (logoGuid ? (
      <img
        className='ple-band__logo-img'
        src={`${env.REACT_APP_API_BASE_URL}/dl/${logoGuid}`}
        alt='Logo'
        style={{
          height: hasHeight ? logoHeight : undefined,
          width: hasWidth ? logoWidth : undefined,
          maxHeight: hasHeight ? logoHeight : undefined,
          objectFit: isCustomLogoStyle ? 'fill' : 'contain',
          }}
      />
    ) : (
      <span className='ple-band__logo'>Logo</span>
    ));

  const text = showText && (
    <span className='ple-band__text'>
      {config.text || (isHeader ? 'Header text' : 'Footer text')}
    </span>
  );

  return (
    <div
      className={`ple-band ple-band--${variant} ${border ? 'is-bordered' : ''}`}
    >
      <div className='ple-band__preview' style={{ border }}>
        <div
          className={`ple-band__content ${
            separate ? 'ple-band__content--split' : ''
          }`}
          style={
            separate
              ? undefined
              : { justifyContent: split ? logoAlign : config.justifyContent }
          }
        >
          {separate ? (
            <>
              <span className='ple-band__slot' style={CELL[logoAlign]}>
                {logo}
              </span>
              <span className='ple-band__slot' style={CELL[textAlign]}>
                {text}
              </span>
            </>
          ) : (
            <>
              {logo}
              {text}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeaderFooterBlock;
